/* PAGINA 5 · GRUPPI DI BACKTEST
 *
 * Un gruppo è una griglia di parametri: alcuni hanno decine di migliaia di
 * righe. Da qui discendono le tre scelte che contano.
 *
 * 1. L'ordinamento è una QUERY. `GET /backtester/search` è l'unico endpoint
 *    paginato del backend e accetta `backtestGroupId`: si ordina e si pagina
 *    lì, la lista si blocca mentre interroga, e le intestazioni di colonna non
 *    sono cliccabili perché prometterebbero un riordino istantaneo.
 *
 * 2. Il gruppo ha il PROPRIO ordinamento, non ereditato dalla ricerca
 *    generale: ereditarlo faceva cambiare in silenzio quale configurazione è
 *    la #1, arrivando da un'altra schermata.
 *
 * 3. La scelta delle colonne esiste perché le metriche sono tante e su un
 *    telefono non ci stanno. Vale anche sulle schede, non solo sulla tabella.
 *
 * `/gruppi` è l'indice, `/gruppi/:id` il dettaglio: stessa schermata, come
 * previsto dal router.
 */

import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Avvertenza,
  ControlloOrdinamento,
  type OrdineScelto,
  Distintivo,
  IconaStrategia,
  GraficoRendimentoRischio,
  Paginazione,
  Pulsante,
  ScheletroLista,
  type CriterioOrdinamento,
} from '../componenti';
import { backtest, gruppiBacktest } from '../api';
import { usaRichiesta } from '../api/usaRichiesta';
import type {
  BacktestGroup,
  RisultatoRicercaBacktest,
} from '../api/tipi';
import { num } from '../lib/formato';
import { ElencoGruppi, SelettoreGruppo, usaGruppi } from './gruppi/SelettoreGruppo';
import { SelezioneColonne } from './gruppi/SelezioneColonne';
import { TabellaConfigurazioni } from './gruppi/TabellaConfigurazioni';
import { quanteAccese, usaSceltaColonne } from './gruppi/colonne';
import { coppiaDiGruppo, periodoDiGruppo, timeframeDiGruppo } from './gruppi/dati';
import './gruppi/pagine.css';

const PER_PAGINA = 25;

/**
 * I criteri che il server accetta davvero su questo insieme. `intorno` è in
 * cima con profitto e drawdown perché su una griglia è la colonna che separa
 * un altopiano da un picco.
 *
 * Manca "A pari rischio", e la mancanza è sostanziale: la classifica che
 * STATO.md indica come quella vera non è chiedibile al server, perché
 * `sortBy` non la prevede. Sotto la lista c'è scritto, invece di lasciarla
 * credere ottenibile con un ordinamento qualsiasi.
 */
const CRITERI_GRUPPO: CriterioOrdinamento[] = [
  { chiave: 'strategyChange', etichetta: 'Profitto', versoNaturale: 'DESC' },
  { chiave: 'drawdownMaxValue', etichetta: 'Drawdown', versoNaturale: 'ASC' },
  { chiave: 'intorno', etichetta: 'Intorno', versoNaturale: 'DESC' },
  { chiave: 'walkForwardScore', etichetta: 'Tenuta', versoNaturale: 'DESC' },
  { chiave: 'bearYearsAvg', etichetta: 'Nei ribassi', versoNaturale: 'DESC' },
  { chiave: 'calmar', etichetta: 'Calmar', versoNaturale: 'DESC' },
  { chiave: 'sharpe', etichetta: 'Sharpe', versoNaturale: 'DESC' },
  { chiave: 'tradesNumber', etichetta: 'Operazioni', versoNaturale: 'DESC' },
];

export function Gruppi() {
  const { id } = useParams<{ id: string }>();
  const idGruppo = id != null && id !== '' && Number.isFinite(Number(id)) ? Number(id) : null;

  /* L'elenco dei gruppi sta QUI e non dentro il dettaglio: il dettaglio si
     rimonta a ogni cambio di gruppo (vedi la `key` più sotto) e l'elenco
     verrebbe riletto ogni volta. Si accende solo quando serve davvero —
     sull'indice subito, sul dettaglio al primo tocco su «Cambia gruppo». */
  const [serveElenco, setServeElenco] = useState(idGruppo == null);
  const elenco = usaGruppi(serveElenco || idGruppo == null);

  return idGruppo != null ? (
    /* La `key` rimonta il dettaglio quando cambia il gruppo. Senza, pagina e
       ordinamento del gruppo precedente sopravviverebbero: si finirebbe a
       pagina 7 di un gruppo che ne ha tre, con una lista vuota e nessuna
       spiegazione. */
    <DettaglioGruppo
      key={idGruppo}
      idGruppo={idGruppo}
      elenco={elenco}
      onServeElenco={() => setServeElenco(true)}
    />
  ) : (
    <IndiceGruppi elenco={elenco} />
  );
}

type ElencoGruppiRichiesta = ReturnType<typeof usaGruppi>;

/* ==========================================================================
 * INDICE — /gruppi
 * ======================================================================== */

function IndiceGruppi({ elenco }: { elenco: ElencoGruppiRichiesta }) {
  const vaiA = useNavigate();
  const gruppi = elenco.dati?.data ?? [];

  return (
    <div className="sb-cornice">
      <div className="sb-intestazione">
        <div className="sb-intestazione__riga">
          <div style={{ flex: 1, minWidth: 180 }}>
            <h1 className="sb-intestazione__titolo">Gruppi di backtest</h1>
            <div className="sb-intestazione__sotto">
              <span className="sb-nota">
                ogni gruppo è una griglia di parametri · scegline uno per vederne le configurazioni
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="sb-gr-corpo">
        <ElencoGruppi
          gruppi={gruppi}
          stato={elenco.stato}
          messaggioErrore={elenco.errore?.message}
          onRiprova={elenco.ricarica}
          onScegli={(idScelto) => vaiA(`/gruppi/${idScelto}`)}
        />
      </div>
    </div>
  );
}

/* ==========================================================================
 * DETTAGLIO — /gruppi/:id
 * ======================================================================== */

function DettaglioGruppo({
  idGruppo,
  elenco,
  onServeElenco,
}: {
  idGruppo: number;
  elenco: ElencoGruppiRichiesta;
  onServeElenco: () => void;
}) {
  const vaiA = useNavigate();

  const [pagina, setPagina] = useState(1);
  /* L'ordinamento e' una lista: dentro un gruppo le parita' sono ancora piu' frequenti che
     nella lista generale, perche' le righe vengono tutte dalla stessa griglia. */
  const [ordine, setOrdine] = useState<OrdineScelto[]>([
    { chiave: 'strategyChange', verso: 'DESC' },
  ]);
  const [colonneAperte, setColonneAperte] = useState(false);
  const [selettoreAperto, setSelettoreAperto] = useState(false);

  const { scelta, inverti, azzera } = usaSceltaColonne();

  const gruppo = usaRichiesta<BacktestGroup>(() => gruppiBacktest.uno(idGruppo), [idGruppo], {
    eVuoto: () => false,
  });

  const ricerca = usaRichiesta<RisultatoRicercaBacktest>(
    (segnale) =>
      backtest.cerca(
        {
          backtestGroupId: idGruppo,
          page: pagina,
          limit: PER_PAGINA,
          sortBy: ordine.map((o) => o.chiave).join(','),
          sortDir: ordine.map((o) => o.verso).join(','),
        },
        segnale,
      ),
    [idGruppo, pagina, ordine],
    { eVuoto: (r) => r.data.length === 0 },
  );

  const righe = ricerca.dati?.data ?? [];
  const totale = ricerca.dati?.total ?? null;
  const pagineTotali = ricerca.dati?.pageCount ?? 1;
  const g = gruppo.dati;

  const punti = useMemo(
    () =>
      righe
        .filter((r) => r.strategyChange != null && r.drawdownMaxValue != null)
        .map((r) => ({
          id: r.id,
          profitto: r.strategyChange as number,
          drawdown: r.drawdownMaxValue as number,
          etichetta: `#${r.id}`,
        })),
    [righe],
  );

  function cambiaOrdinamento(nuovo: OrdineScelto[]) {
    /* Cambiare criterio significa rifare la query dall'inizio: restare a
       pagina 7 di un ordinamento diverso mostrerebbe righe che non c'entrano
       con quello appena chiesto. */
    setOrdine(nuovo);
    setPagina(1);
  }

  const periodo = g ? periodoDiGruppo(g) : '—';
  const inCorso = ricerca.inAggiornamento;

  return (
    <div className="sb-cornice">
      {/* ---------- testata ---------- */}
      <div className="sb-intestazione">
        <div className="sb-gr-testata">
          {g && <IconaStrategia nome={g.strategyName} dim={30} />}

          <div className="sb-gr-testata__corpo">
            <div className="sb-gr-testata__titolo">
              <h1 className="sb-intestazione__titolo">Gruppo #{num(idGruppo)}</h1>
              {/* Verde e rosso hanno solo significato finanziario: un gruppo
                  finito non è un guadagno. "Completato" resta neutro, "in
                  corso" prende il blu, che è l'accento interattivo. */}
              {g &&
                (g.isRunning ? (
                  <Distintivo tono="accento">in corso</Distintivo>
                ) : (
                  <Distintivo tono="neutro">completato</Distintivo>
                ))}
            </div>
            <div className="sb-nota" style={{ marginTop: 'var(--sb-s-4)' }}>
              {g
                ? `${g.strategyName} · ${coppiaDiGruppo(g)} · ${timeframeDiGruppo(g)} · ${periodo}`
                : gruppo.stato === 'errore'
                  ? 'gruppo non leggibile'
                  : 'caricamento del gruppo…'}
            </div>
          </div>

          <div className="sb-gr-testata__azioni">
            <Pulsante
              onClick={() => {
                onServeElenco();
                setSelettoreAperto(true);
              }}
            >
              Cambia gruppo
            </Pulsante>
            <Pulsante onClick={() => setColonneAperte(true)}>
              Colonne · {num(quanteAccese(scelta))}
            </Pulsante>
          </div>
        </div>
      </div>

      <div className="sb-gr-corpo">
        {gruppo.stato === 'errore' && (
          <Avvertenza livello="grave">
            Il gruppo #{num(idGruppo)} non è leggibile: {gruppo.errore?.message}
          </Avvertenza>
        )}

        {/* ---------- contatori ---------- */}
        <ContatoriGruppo gruppo={g} valutate={totale} statoRicerca={ricerca.stato} />

        {/* ---------- rendimento contro rischio ---------- */}
        <div className="sb-gr-sezione sb-gr-sezione__imbottita">
          <div className="sb-gr-sezione__titolo">Rendimento contro rischio</div>
          <div className="sb-nota" style={{ marginTop: 'var(--sb-s-3)' }}>
            {/* Il grafico disegna la PAGINA, non il gruppo intero: dirlo evita
                di leggere una nuvola di 25 punti come se fossero 87.750. */}
            {num(punti.length)} configurazioni di questa pagina · in alto a sinistra = migliore ·{' '}
            {periodo}
          </div>
          <div style={{ marginTop: 'var(--sb-s-5)' }}>
            <GraficoRendimentoRischio
              punti={punti}
              stato={ricerca.stato === 'pieno' && punti.length === 0 ? 'vuoto' : ricerca.stato}
              causaVuoto="Nessuna configurazione con profitto e drawdown misurati in questa pagina."
              messaggioErrore={ricerca.errore?.message}
              onRiprova={ricerca.ricarica}
              altezza={280}
            />
          </div>
        </div>

        {/* ---------- configurazioni ---------- */}
        <div className="sb-gr-sezione">
          <div className="sb-gr-sezione__testata">
            <div className="sb-gr-sezione__titolo">Configurazioni del gruppo</div>
          </div>

          <ControlloOrdinamento
            criteri={CRITERI_GRUPPO}
            ordine={ordine}
            totale={totale}
            inCorso={inCorso}
            onCambia={cambiaOrdinamento}
            contesto={g ? `gruppo #${num(idGruppo)} · ${periodo}` : undefined}
          />

          <TabellaConfigurazioni
            righe={righe}
            scelta={scelta}
            stato={ricerca.stato}
            causaVuoto={`Il gruppo #${num(idGruppo)} non ha ancora configurazioni valutate.`}
            messaggioErrore={ricerca.errore?.message}
            onRiprova={ricerca.ricarica}
            onApri={(idBacktest) => vaiA(`/backtest/${idBacktest}`)}
            primaPosizione={(pagina - 1) * PER_PAGINA}
            interrogazioneInCorso={inCorso ? 'nuova query sul server…' : null}
          />

          {ricerca.stato === 'pieno' && (
            <Paginazione
              pagina={pagina}
              pagineTotali={pagineTotali}
              perPagina={PER_PAGINA}
              totale={totale}
              inCorso={inCorso}
              onVai={setPagina}
            />
          )}

          <div className="sb-gr-sezione__imbottita">
            {/* Degradazione dichiarata: il server non ordina per rischio
                normalizzato, quindi la classifica che conta si può leggere
                solo dentro la pagina caricata. */}
            <Avvertenza livello="nota">
              «A pari rischio» si vede su ogni riga ma non si può ordinare: il server accetta solo
              profitto, drawdown, intorno, tenuta, nei ribassi, Calmar, Sharpe e operazioni. La
              classifica per rischio normalizzato vale quindi dentro questa pagina, non su tutte le{' '}
              {totale == null ? '—' : num(totale)} configurazioni.
            </Avvertenza>
          </div>
        </div>
      </div>

      <SelezioneColonne
        aperta={colonneAperte}
        onChiudi={() => setColonneAperte(false)}
        scelta={scelta}
        onInverti={inverti}
        onAzzera={azzera}
      />

      <SelettoreGruppo
        aperto={selettoreAperto}
        onChiudi={() => setSelettoreAperto(false)}
        gruppi={elenco.dati?.data ?? []}
        idAttivo={idGruppo}
        stato={elenco.stato}
        messaggioErrore={elenco.errore?.message}
        onRiprova={elenco.ricarica}
      />
    </div>
  );
}

/* ==========================================================================
 * CONTATORI
 * ======================================================================== */

function ContatoriGruppo({
  gruppo,
  valutate,
  statoRicerca,
}: {
  gruppo: BacktestGroup | null;
  valutate: number | null;
  statoRicerca: 'caricamento' | 'pieno' | 'vuoto' | 'errore';
}) {
  if (!gruppo && statoRicerca === 'caricamento') {
    return <ScheletroLista righe={2} conIcona={false} />;
  }

  const previste = gruppo?.backtestNumber ?? null;
  const mancanti = previste != null && valutate != null ? Math.max(0, previste - valutate) : null;

  return (
    <div className="sb-gr-contatori">
      <Contatore
        etichetta="CONFIGURAZIONI"
        valore={previste == null ? '—' : num(previste)}
        sotto="dichiarate dal gruppo"
      />
      <Contatore
        etichetta="VALUTATE"
        valore={valutate == null ? '—' : num(valutate)}
        sotto="righe salvate e interrogabili"
      />
      <Contatore
        etichetta="MANCANTI"
        valore={mancanti == null ? '—' : num(mancanti)}
        /* Non è "fermate prima": `wasStoppedEarly` sta solo sull'entity
           completa e la ricerca paginata non lo restituisce. Dire "fermate
           prima" sarebbe un'etichetta inventata su un numero che è solo una
           differenza. */
        sotto="differenza fra dichiarate e valutate"
        tono="cautela"
      />
      <Contatore
        etichetta="DURATA"
        valore={durataGruppo(gruppo)}
        sotto={gruppo?.isRunning ? 'ancora in corso' : 'dal primo all’ultimo salvataggio'}
      />
    </div>
  );
}

function Contatore({
  etichetta,
  valore,
  sotto,
  tono = 'neutro',
}: {
  etichetta: string;
  valore: string;
  sotto: string;
  tono?: 'neutro' | 'cautela';
}) {
  return (
    <div className="sb-gr-contatore">
      <div className="sb-gr-contatore__etichetta">{etichetta}</div>
      <div className={`sb-gr-contatore__valore sb-tono-${tono}`}>{valore}</div>
      <div className="sb-gr-contatore__sotto">{sotto}</div>
    </div>
  );
}

/** Quanto è durata l'ottimizzazione, dedotta da `createdAt` e `updatedAt`: non
 *  esiste un endpoint di avanzamento, quindi è l'unica misura disponibile. */
function durataGruppo(gruppo: BacktestGroup | null): string {
  if (!gruppo?.createdAt) return '—';
  const inizio = new Date(gruppo.createdAt).getTime();
  const fine = gruppo.isRunning
    ? Date.now()
    : new Date(gruppo.updatedAt ?? gruppo.createdAt).getTime();
  if (!Number.isFinite(inizio) || !Number.isFinite(fine) || fine < inizio) return '—';

  const minuti = Math.round((fine - inizio) / 60000);
  if (minuti < 60) return `${minuti}m`;
  const ore = Math.floor(minuti / 60);
  if (ore < 24) return `${ore}h ${minuti % 60}m`;
  const giorni = Math.floor(ore / 24);
  return `${giorni}g ${ore % 24}h`;
}
